// src/pages/LandingPage.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    FaPhone, 
    FaMapMarkerAlt, 
    FaArrowRight, 
    FaShieldAlt, 
    FaChartLine, 
    FaUsers, 
    FaBell, 
    FaCreditCard, 
    FaLock, 
    FaMobileAlt, 
    FaWhatsapp, 
    FaEnvelope, 
    FaSun, 
    FaMoon, 
    FaFacebook, 
    FaInstagram, 
    FaYoutube 
} from 'react-icons/fa';
import './LandingPage.css';

const LandingPage = () => {
    // Estado para el modo Claro / Oscuro (Tema claro por defecto)
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
    
    // Estados para la calculadora de simulación
    const [houses, setHouses] = useState(150);
    const [automated, setAutomated] = useState(false);

    // Estado para las pestañas de información breves
    const [activeTab, setActiveTab] = useState(0);

    // Estado para el fondo parallax interactivo
    const [coords, setCoords] = useState({ x: 0, y: 0 });

    useEffect(() => {
        // Actualizar el título de la página para SEO
        document.title = 'Domus Habita - Software de Gestión Inteligente para Fraccionamientos en México';
    }, []);

    // Guardar preferencia de tema
    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
    };

    // Capturar coordenadas del cursor a nivel de página
    const handleMouseMove = (e) => {
        const { clientX, clientY } = e;
        const x = (clientX - window.innerWidth / 2) / 50;
        const y = (clientY - window.innerHeight / 2) / 50;
        setCoords({ x, y });
    };

    const handleCallClick = (phoneNumber) => {
        window.location.href = `tel:${phoneNumber}`;
    };

    const handleWhatsAppClick = (phone) => {
        window.open(`https://wa.me/${phone}`, '_blank');
    };

    const handleEmailClick = (emailAddress) => {
        window.location.href = `mailto:${emailAddress}`;
    };

    const handleDemoClick = () => {
        window.location.href = '/login?view=requestAccess';
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Datos de las pestañas
    const tabData = [
        {
            title: "Software para Fraccionamientos",
            heading: "Software para Fraccionamientos",
            desc: "Administra de manera integral y segura. Nuestro sistema web y móvil centraliza el control de residentes, bitácoras de visitas, administración de personal y reportes en tiempo real, todo desde una interfaz moderna disponible las 24 horas."
        },
        {
            title: "Administración Condominios",
            heading: "Administración de Condominios",
            desc: "Simplifica las finanzas del residencial. Genera cuotas ordinarias y extraordinarias automáticas, gestiona egresos e ingresos con transparencia, y automatiza el seguimiento de morosidad con notificaciones en tiempo real para residentes."
        },
        {
            title: "Control de Acceso Inteligente",
            heading: "Control de Acceso Inteligente",
            desc: "Integración automática de software con paneles de acceso físicos (ZKTeco, SEG, Dahua, Syscom, etc.). El sistema realiza la desactivación automática de tags al llegar el día programado de corte, eliminando el error humano y reduciendo la carga de trabajo directa del administrador."
        }
    ];

    // Lógica tarifaria
    const getCost = (qty, isAutomated) => {
        let total = 0;
        let perHouse = 0;
        let tierLabel = '';

        if (qty <= 150) {
            total = 1500;
            perHouse = Number((total / qty).toFixed(2));
            tierLabel = 'Tarifa Fija (Menores de 150 casas siempre será $1,500 MXN al mes)';
        } else if (qty <= 300) {
            total = qty * 11.5;
            perHouse = 11.5;
            tierLabel = 'Plan Mediano ($11.50 MXN por vivienda)';
        } else if (qty <= 999) {
            total = qty * 10.0;
            perHouse = 10.0;
            tierLabel = 'Plan Grande ($10.00 MXN por vivienda)';
        } else {
            total = qty * 9.0;
            perHouse = 9.0;
            tierLabel = 'Plan Premium ($9.00 MXN por vivienda)';
        }

        if (isAutomated) {
            total = total * 1.16;
            perHouse = Number((perHouse * 1.16).toFixed(2));
        }

        return {
            total: Math.round(total),
            perHouse,
            tierLabel
        };
    };

    const currentCost = getCost(houses, automated);

    return (
        <div 
            className={`landing-page ${theme === 'dark' ? 'theme-dark' : ''}`}
            onMouseMove={handleMouseMove}
        >
            {/* Parallax Background Layers */}
            <div className="landing-bg-parallax">
                <div 
                    className="parallax-city-silhouette" 
                    style={{ 
                        transform: `translate3d(${coords.x * 0.3}px, 0, 0)` 
                    }}
                />
                <div 
                    className="parallax-triangles-grid"
                    style={{ 
                        transform: `translate3d(${coords.x * 0.7}px, ${coords.y * 0.7}px, 0)` 
                    }}
                >
                    <div className="geo-triangle triangle-1"></div>
                    <div className="geo-triangle triangle-2"></div>
                    <div className="geo-triangle triangle-3"></div>
                    <div className="geo-triangle triangle-4"></div>
                    <div className="geo-triangle triangle-5"></div>
                    <div className="geo-triangle triangle-6"></div>
                    <div className="geo-triangle triangle-7"></div>
                    <div className="geo-triangle triangle-8"></div>
                    <div className="geo-triangle triangle-9"></div>
                    <div className="geo-triangle triangle-10"></div>
                    <div className="geo-triangle triangle-11"></div>
                    <div className="geo-triangle triangle-12"></div>
                    <div className="geo-triangle triangle-13"></div>
                    <div className="geo-triangle triangle-14"></div>
                    <div className="geo-triangle triangle-15"></div>
                </div>
            </div>

            {/* Header con navegación */}
            <header className="landing-header">
                <div className="landing-header-content">
                    <div className="landing-logo-section">
                        <img src="/logo.png" alt="Domus Habita" className="landing-logo" />
                        <div className="landing-logo-text">
                            <h2 className="landing-brand-title">Domus Habita</h2>
                            <p className="landing-brand-tagline">El Software de Gestión Inteligente para Fraccionamientos en México</p>
                        </div>
                    </div>
                    
                    <div className="landing-header-actions">
                        {/* Botón de cambio de tema Claro/Oscuro */}
                        <button 
                            className="theme-toggle-btn" 
                            onClick={toggleTheme}
                            title={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
                            aria-label="Cambiar tema de color"
                        >
                            {theme === 'light' ? (
                                <FaMoon className="theme-toggle-icon" />
                            ) : (
                                <FaSun className="theme-toggle-icon" />
                            )}
                        </button>

                        <Link to="/login" className="landing-login-btn">
                            <FaLock />
                            <span>Domus Habita | Acceso</span>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="landing-hero">
                <div className="landing-hero-content">
                    <h1 className="landing-hero-title">
                        Administración Condominal Moderna y Convivencia Armoniosa
                    </h1>
                    <p className="landing-hero-description">
                        La solución tecnológica más completa y avanzada para la gestión de fraccionamientos residenciales en
                        México. Diseñado desde la experiencia para simplificar tus procesos diarios.
                    </p>

                    {/* Contenedor de Pestañas Informativas */}
                    <div className="hero-tabs-container">
                        <div className="hero-tabs-header">
                            {tabData.map((tab, idx) => (
                                <button
                                    key={idx}
                                    className={`hero-tab-btn ${activeTab === idx ? 'active' : ''}`}
                                    onClick={() => setActiveTab(idx)}
                                >
                                    {tab.title}
                                </button>
                            ))}
                        </div>
                        <div className="hero-tab-content">
                            <div key={activeTab} className="tab-pane fade-in">
                                <h3>{tabData[activeTab].heading}</h3>
                                <p>{tabData[activeTab].desc}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Beneficios */}
            <section id="beneficios" className="landing-benefits">
                <h3 className="landing-section-title">Beneficios al Contratar Domus Habita</h3>
                <div className="benefits-grid">
                    <div className="benefit-card card-seguridad">
                        <FaShieldAlt className="benefit-icon" />
                        <h3>Seguridad Avanzada</h3>
                        <p>Control de acceso inteligente, registro de visitas, gestión de vigilancia y monitoreo en tiempo real.</p>
                    </div>
                    <div className="benefit-card card-finanzas">
                        <FaChartLine className="benefit-icon" />
                        <h4>Gestión Financiera Completa</h4>
                        <p>Control de pagos, adeudos, recibos automáticos, reportes financieros y seguimiento de morosidad.</p>
                    </div>
                    <div className="benefit-card card-residentes">
                        <FaUsers className="benefit-icon" />
                        <h3>Administración de Residentes</h3>
                        <p>Gestión completa de propietarios, inquilinos, casas, lotes y toda la información de contacto.</p>
                    </div>
                    <div className="benefit-card card-comunicacion">
                        <FaBell className="benefit-icon" />
                        <h3>Comunicación Comunitaria</h3>
                        <p>Chat comunitario, avisos, votaciones, propuestas, incidencias y eventos en un solo lugar.</p>
                    </div>
                    <div className="benefit-card card-pagos">
                        <FaCreditCard className="benefit-icon" />
                        <h4>Pagos Automatizados</h4>
                        <p>Sistema de cargos mensuales automáticos, generación de recibos y notificaciones de pagos.</p>
                    </div>
                    <div className="benefit-card card-movil">
                        <FaMobileAlt className="benefit-icon" />
                        <h3>Aplicación Móvil</h3>
                        <p>Acceso completo desde dispositivos móviles Android para administradores y residentes.</p>
                    </div>
                </div>
            </section>

            {/* Ubicación Local y Valor en Trinchera */}
            <section className="landing-location">
                <div className="landing-location-content">
                    <div className="location-grid">
                        <div className="location-text-col">
                            <span className="location-tag">El Plus de Nuestra Experiencia</span>
                            <h2 className="location-title-highlight">Desarrollado en México para Fraccionamientos Mexicanos</h2>
                            <p className="location-desc-primary">
                                Domus Habita no es un sistema genérico. <strong>Nos forjamos directamente en la trinchera</strong> de la operación condominal, viviendo el día a día de los fraccionamientos residenciales.
                            </p>
                            <p className="location-desc-secondary">
                                Entendemos la realidad del flujo de trabajo: desde la gestión de incidencias críticas en la caseta de vigilancia con guardias de seguridad, hasta el manejo minucioso y transparente de las finanzas y adeudos en las oficinas administrativas. Esta experiencia directa es la que nos impulsa a diseñar soluciones reales que eliminan la burocracia, previenen desvíos y fortalecen la convivencia vecinal.
                            </p>
                        </div>
                        <div className="location-visual-col">
                            <div className="trinchera-badge-card">
                                <div className="badge-header">
                                    <FaMapMarkerAlt className="badge-icon-ping" />
                                    <span>Presencia Nacional</span>
                                </div>
                                <div className="badge-body">
                                    <h3>Desarrollo Local</h3>
                                    <p>Soporte de ingeniería inmediato desde San Juan del Río, Querétaro. Diseñado conforme al marco normativo de México.</p>
                                    <div className="mexico-colors-strip">
                                        <div className="stripe-green"></div>
                                        <div className="stripe-white"></div>
                                        <div className="stripe-red"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Planes, Precios y Calculadora Interactiva */}
            <section id="precios" className="landing-pricing">
                <h2 className="landing-section-title">Planes a tu Medida</h2>
                
                {/* Calculadora de Simulación Reestructurada (Evitando sobreposición) */}
                <div className="landing-calculator-container">
                    <div className="landing-calculator-header">
                        <h3>Simula el Costo de tu Fraccionamiento</h3>
                        <p>Ajusta el número de viviendas y activa la automatización para conocer tu tarifa mensual al instante.</p>
                    </div>
                    
                    <div className="landing-calculator-grid">
                        <div className="landing-calculator-controls">
                            
                            {/* Bloque 1: Slider del Número de Viviendas con Escala */}
                            <div className="landing-control-card-block">
                                <label className="landing-control-block-label" htmlFor="houses-slider">
                                    Número de Domicilios: <span className="highlight-value">{houses}</span> {houses === 1 ? 'vivienda' : 'viviendas'}
                                </label>
                                <div className="landing-slider-wrapper-layout">
                                    <input 
                                        id="houses-slider"
                                        type="range" 
                                        min="1" 
                                        max="1200" 
                                        value={houses} 
                                        onChange={(e) => setHouses(Number(e.target.value))}
                                        className="landing-range-slider"
                                    />
                                    <div className="landing-slider-labels">
                                        <span>1</span>
                                        <span>300</span>
                                        <span>600</span>
                                        <span>900</span>
                                        <span>1200+</span>
                                    </div>
                                </div>
                            </div>

                            {/* Bloque 2: Entrada Numérica Directa en su propia fila */}
                            <div className="landing-control-card-block landing-inline-number-block">
                                <span className="landing-control-block-info">Escribe la cantidad exacta de domicilios:</span>
                                <input 
                                    type="number" 
                                    min="1" 
                                    max="5000" 
                                    value={houses} 
                                    onChange={(e) => setHouses(Math.max(1, Number(e.target.value)))}
                                    className="landing-exact-number-input"
                                />
                            </div>

                            {/* Bloque 3: Switch de Automatización con más espacio y Clase Activa */}
                            <div className={`landing-control-card-block landing-automation-switch-block ${automated ? 'active-automation' : ''}`}>
                                <div className="landing-automation-text-info">
                                    <span className="landing-control-block-label">Control de Acceso Automatizado</span>
                                    <p className="landing-automation-helper-text">Integración inteligente del software con tus paneles físicos (ZKTeco, SEG, Dahua, Syscom, etc.) para la activación y desactivación de tags automática.</p>
                                </div>
                                <label className="landing-switch-container">
                                    <input 
                                        type="checkbox" 
                                        checked={automated} 
                                        onChange={(e) => setAutomated(e.target.checked)}
                                    />
                                    <span className="landing-switch-slider"></span>
                                </label>
                            </div>
                        </div>

                        {/* Bloque de Resultados */}
                        <div className="landing-calculator-result">
                            <div className="landing-result-card">
                                <span className="landing-result-title">Inversión Mensual Estimada</span>
                                <div className="landing-result-price">
                                    <span className="currency">$</span>
                                    <span className="amount">{currentCost.total.toLocaleString()}</span>
                                    <span className="period">MXN/mes</span>
                                </div>
                                <div className="landing-result-detail">
                                    <p>Costo promedio por domicilio: <strong>${currentCost.perHouse} MXN</strong> al mes</p>
                                    <span className="tier-tag">{currentCost.tierLabel}</span>
                                    {automated && <span className="automated-tag-badge">Incluye 16% por Automatización de Accesos</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Resumen de Tarifas Base */}
                <div className="pricing-base-tiers">
                    <h4>Estructura Tarifaria Base (Sin Control de Acceso Automatizado):</h4>
                    <div className="base-tiers-grid">
                        <div className="tier-card-info">
                            <h5>1 a 150 viviendas</h5>
                            <p className="tier-price-val">$1,500 MXN / mes</p>
                            <p className="tier-desc-val">Costo fijo mensual total</p>
                        </div>
                        <div className="tier-card-info">
                            <h5>151 a 300 viviendas</h5>
                            <p className="tier-price-val">$11.50 MXN</p>
                            <p className="tier-desc-val">Al mes por vivienda</p>
                        </div>
                        <div className="tier-card-info">
                            <h5>301 a 999 viviendas</h5>
                            <p className="tier-price-val">$10.00 MXN</p>
                            <p className="tier-desc-val">Al mes por vivienda</p>
                        </div>
                        <div className="tier-card-info">
                            <h5>1000+ viviendas</h5>
                            <p className="tier-price-val">$9.00 MXN</p>
                            <p className="tier-desc-val">Tarifa preferencial por vivienda</p>
                        </div>
                    </div>
                </div>

                <div className="pricing-note-section">
                    <p className="pricing-disclaimer">
                        <strong>Nota importante:</strong> Todos los planes incluyen todos los módulos activos por defecto.
                        Cada módulo puede desactivarse individualmente, economizando un 5% del costo total por módulo desactivado. El uso de control de acceso automatizado con panel incrementa un 16% sobre las tarifas base debido a la sincronización e infraestructura en la nube requerida.
                    </p>
                </div>
            </section>

            {/* CTA Section (Con fondo en constante movimiento) */}
            <section className="landing-cta">
                <div className="cta-content">
                    <h2 className="cta-title">¿Listo para Transformar la Gestión de tu Fraccionamiento?</h2>
                    <p className="cta-description">
                        Solicita una demostración gratuita y descubre cómo Domus Habita puede optimizar
                        la administración y seguridad de tu residencial.
                    </p>
                    <div className="cta-buttons">
                        <div className="cta-contact-group">
                            <button onClick={() => handleCallClick('+524142263804')} className="cta-btn cta-btn-call">
                                <FaPhone />
                                <span>Llamar: 414-226-3804</span>
                            </button>
                            <div className="cta-contact-options">
                                <button onClick={() => handleWhatsAppClick('524142263804')} className="cta-contact-btn" title="Enviar WhatsApp">
                                    <FaWhatsapp />
                                    <span>WhatsApp</span>
                                </button>
                                <button onClick={() => handleEmailClick('domushabitat2025@gmail.com')} className="cta-contact-btn" title="Enviar Email">
                                    <FaEnvelope />
                                    <span>Email</span>
                                </button>
                            </div>
                            <p className="cta-contact-info">domushabitat2025@gmail.com</p>
                        </div>
                        <button onClick={handleDemoClick} className="cta-btn cta-btn-demo">
                            <FaArrowRight />
                            <span>Solicitar Demo Gratuita</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* Pie de Página Reestructurado (Estilo PROSIF y corregido) */}
            <footer className="landing-footer-modern">
                <div className="footer-main-content">
                    {/* Columna Izquierda: Logo y Breve Descripción */}
                    <div className="footer-col footer-col-about">
                        <div className="footer-logo-row">
                            <img src="/logo.png" alt="Domus Habita" className="footer-logo-img" />
                            <h3>Domus Habita</h3>
                        </div>
                        <p className="footer-about-text">
                            Software inteligente para la gestión de fraccionamientos, control de accesos automatizado y convivencia condominal en México. San Juan del Río, Querétaro.
                        </p>
                        <div className="footer-social-icons">
                            <a href="#" className="social-icon-link disabled" title="Próximamente en Facebook" onClick={(e) => e.preventDefault()}>
                                <FaFacebook />
                            </a>
                            <a href="https://www.instagram.com/domus.habitat/" className="social-icon-link" target="_blank" rel="noopener noreferrer" title="Síguenos en Instagram">
                                <FaInstagram />
                            </a>
                            <a href="#" className="social-icon-link disabled" title="Próximamente en YouTube" onClick={(e) => e.preventDefault()}>
                                <FaYoutube />
                            </a>
                        </div>
                    </div>

                    {/* Columna Central: Navegación */}
                    <div className="footer-col footer-col-links">
                        <h4>NAVEGACIÓN</h4>
                        <ul className="footer-links-list">
                            <li><a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({top: 0, behavior: 'smooth'}); }}>Inicio</a></li>
                            <li><a href="#beneficios" onClick={(e) => { e.preventDefault(); scrollToSection('beneficios'); }}>Beneficios</a></li>
                            <li><a href="#precios" onClick={(e) => { e.preventDefault(); scrollToSection('precios'); }}>Planes y Precios</a></li>
                            <li><Link to="/aviso-privacidad">Aviso de Privacidad</Link></li>
                            <li><a href="#" onClick={(e) => { e.preventDefault(); alert('Próximamente disponibles los Términos y Condiciones completos.'); }}>Términos y Condiciones</a></li>
                            <li><Link to="/login" className="footer-portal-link">Portal Residentes / Acceso →</Link></li>
                        </ul>
                    </div>

                    {/* Columna Derecha: Contacto Directo Corregido */}
                    <div className="footer-col footer-col-contact">
                        <h4>CONTACTO DIRECTO</h4>
                        
                        <div className="contact-subblock">
                            <h5>TELÉFONOS</h5>
                            <p onClick={() => handleCallClick('+524271309138')} className="interactive-contact-item"><strong>427 130 9138</strong> <span className="contact-label">(Ing. Jonnatan)</span></p>
                            <p onClick={() => handleCallClick('+524142263804')} className="interactive-contact-item"><strong>414 226 3804</strong> <span className="contact-label">(Ing. Jonnatan)</span></p>
                        </div>

                        <div className="contact-subblock">
                            <h5>CORREOS ELECTRÓNICOS</h5>
                            <p onClick={() => handleEmailClick('wilbo_him@hotmail.com')} className="interactive-contact-item email-item">wilbo_him@hotmail.com</p>
                            <p onClick={() => handleEmailClick('domushabitat2025@gmail.com')} className="interactive-contact-item email-item">domushabitat2025@gmail.com</p>
                        </div>

                        <div className="contact-subblock">
                            <h5>OFICINA PRINCIPAL</h5>
                            <p className="office-address">Cuarzo #200, Infonavit Pedregoso, San Juan del Río, Qro.</p>
                        </div>
                    </div>
                </div>

                {/* Fila Inferior de Derechos y Atribuciones */}
                <div className="footer-bottom-bar">
                    <div className="footer-bottom-content">
                        <p className="copyright-txt">
                            © 2026 PROSIF (Proveedores de Servicios Integrales y Funcionales). Todos los derechos reservados.
                        </p>
                        <p className="design-attribution-txt">
                            Diseñado por <strong>DOMUS HABITAT</strong>.
                        </p>
                    </div>
                </div>
            </footer>

            {/* Botón Flotante de WhatsApp */}
            <a 
                href="https://wa.me/524142263804" 
                className="whatsapp-floating-button" 
                target="_blank" 
                rel="noopener noreferrer"
                title="Chatear por WhatsApp con Soporte"
            >
                <FaWhatsapp />
            </a>
        </div>
    );
};

export default LandingPage;
